/**
 * Debug Logger pour @ollaid/native-sso
 * Stocke l'historique des appels API en mémoire pour le DebugPanel
 * @version 2.7.9
 */

export interface ApiCallLog {
  id: string;
  timestamp: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  requestBody: unknown;
  requestHeaders?: Record<string, string>;
  responseStatus: number | null;
  responseBody: unknown;
  duration: number;
  error?: string;
}

const MAX_LOGS = 50;
const STORAGE_KEY = 'ollaid.native-sso.debug-logs.v1';
let apiLogs: ApiCallLog[] = [];
let listeners: ((logs: ApiCallLog[]) => void)[] = [];

const REDACTED = '[redacted]';

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    normalized.includes('token') ||
    normalized.includes('password') ||
    normalized.includes('secret') ||
    normalized.includes('authorization') ||
    normalized.includes('otp') ||
    normalized.includes('code') ||
    normalized.includes('cookie') ||
    normalized.includes('credential') ||
    normalized.includes('app_access') ||
    normalized.includes('sanctum')
  );
}

function sanitizeValue(value: unknown, keyHint = ''): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (keyHint && isSensitiveKey(keyHint)) return REDACTED;
    if (/^Bearer\s+/i.test(value)) return REDACTED;
    if (/^[0-9]+\|[A-Za-z0-9]/.test(value)) return REDACTED;
    return value;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(item => sanitizeValue(item));

  const input = value as Record<string, unknown>;
  const output: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(input)) {
    output[key] = isSensitiveKey(key) ? REDACTED : sanitizeValue(child, key);
  }
  return output;
}

function notifyListeners() {
  listeners.forEach(fn => fn([...apiLogs]));
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function persistLogs(): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(apiLogs));
  } catch {
    // ignore storage quota / security failures
  }
}

function loadPersistedLogs(): ApiCallLog[] {
  const storage = getSessionStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(Boolean)
      .slice(0, MAX_LOGS)
      .map((item) => ({
        id: String(item?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
        timestamp: String(item?.timestamp ?? new Date().toISOString()),
        method: item?.method === 'GET' || item?.method === 'POST' || item?.method === 'PUT' || item?.method === 'DELETE'
          ? item.method
          : 'GET',
        endpoint: String(item?.endpoint ?? ''),
        requestBody: item?.requestBody ?? null,
        requestHeaders: item?.requestHeaders && typeof item.requestHeaders === 'object'
          ? item.requestHeaders
          : undefined,
        responseStatus: typeof item?.responseStatus === 'number' ? item.responseStatus : null,
        responseBody: item?.responseBody ?? null,
        duration: typeof item?.duration === 'number' ? item.duration : 0,
        error: typeof item?.error === 'string' ? item.error : undefined,
      }));
  } catch {
    return [];
  }
}

apiLogs = loadPersistedLogs();

export function logApiCall(
  method: ApiCallLog['method'],
  endpoint: string,
  requestBody: unknown,
  requestHeaders?: Record<string, string>
): string {
  const id = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const log: ApiCallLog = {
    id,
    timestamp: new Date().toISOString(),
    method,
    endpoint,
    requestBody: sanitizeValue(requestBody),
    requestHeaders: requestHeaders ? (sanitizeValue(requestHeaders) as Record<string, string>) : undefined,
    responseStatus: null,
    responseBody: null,
    duration: 0,
  };

  apiLogs.unshift(log);
  if (apiLogs.length > MAX_LOGS) {
    apiLogs = apiLogs.slice(0, MAX_LOGS);
  }
  persistLogs();
  notifyListeners();
  return id;
}

export function logApiResponse(
  logId: string,
  responseStatus: number,
  responseBody: unknown,
  startTime: number,
  error?: string
): void {
  const log = apiLogs.find(l => l.id === logId);
  if (log) {
    log.responseStatus = responseStatus;
    log.responseBody = sanitizeValue(responseBody);
    log.duration = Date.now() - startTime;
    log.error = error;
    persistLogs();
    notifyListeners();
  }
}

export function getApiLogs(): ApiCallLog[] {
  return [...apiLogs];
}

export function clearApiLogs(): void {
  apiLogs = [];
  persistLogs();
  notifyListeners();
}

export function subscribeToLogs(callback: (logs: ApiCallLog[]) => void): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter(fn => fn !== callback);
  };
}

export function exportLogsAsText(): string {
  if (apiLogs.length === 0) return 'Aucun appel API enregistré.';

  const lines: string[] = [
    '='.repeat(60),
    'NATIVE SSO DEBUG LOGS',
    `Exporté le: ${new Date().toISOString()}`,
    '='.repeat(60),
    '',
  ];

  apiLogs.slice().reverse().forEach((log, index) => {
    const emoji = log.responseStatus === null ? '⏳' : (log.responseStatus >= 200 && log.responseStatus < 300 ? '✅' : '❌');
    lines.push(`[${index + 1}] ${emoji} ${log.method} ${log.endpoint}`);
    lines.push(`    Status: ${log.responseStatus ?? 'En cours...'} | Durée: ${log.duration}ms`);
    if (log.requestBody) lines.push(`    Request: ${JSON.stringify(log.requestBody)}`);
    if (log.responseBody) lines.push(`    Response: ${JSON.stringify(log.responseBody)}`);
    if (log.error) lines.push(`    ⚠️ Error: ${log.error}`);
    lines.push('');
  });

  return lines.join('\n');
}
